import { globalizeSelectors } from '../Utils';
import {
  FEATURE_PRO,
  FEATURE_POS_MATCHING,
  FEATURE_IST_PRO,
  PRIVILEGE_MATCHING,
  offline_mode,
  FEATURE_TSR,
} from '../../Constants';
import type {
  ActivationCertificate,
  LicenseCode,
  LicenseStatusResponse,
  ProtectionAction,
  ProtectionActionResultPayload,
  ProtectionState,
} from '../types/Protection';
/**
 * Just a simple flag to notify the UI that progress modal needs to be shown!
 */

/*
 * action types
 */

const types = {
  FETCH_STATUS: 'PROTECTION/FETCH_STATUS',
  FETCH_STATUS_REJECTED: 'PROTECTION/FETCH_STATUS_REJECTED',
  FETCH_STATUS_STARTED: 'PROTECTION/FETCH_STATUS_STARTED',
  FETCH_STATUS_FULLFILLED: 'PROTECTION/FETCH_STATUS_FULLFILLED',
  ACTIVATE_ONLINE: 'PROTECTION/ACTIVATE_ONLINE',
  DEACTIVATE_ONLINE: 'PROTECTION/DEACTIVATE_ONLINE',
  ACTIVATE_CERT_REQUEST: 'PROTECTION/ACTIVATE_CERT_REQUEST',
  ACTIVATE_CERT_REQUEST_RESET: 'PROTECTION/ACTIVATE_CERT_REQUEST_RESET',
  DEACTIVATION_CERT_REQUEST: 'PROTECTION/DEACTIVATION_CERT_REQUEST',
  MANUAL_ACTIVATION: 'PROTECTION/MANUAL_ACTIVATION',
  ACTION_STARTED: 'PROTECTION/ACTION_STARTED',
  ACTION_FULLFILLED: 'PROTECTION/ACTION_FULLFILLED',
  ACTION_REJECTED: 'PROTECTION/ACTION_REJECTED',
} as const;

function payloadAs<Payload>(action: ProtectionAction): Payload {
  return action.payload as Payload;
}

/*
 * action creators
 */

const actions = {
  fetchStatus: (): ProtectionAction => ({ type: types.FETCH_STATUS }),
  activateOnline: (
    license_code: LicenseCode
  ): ProtectionAction<LicenseCode> => ({
    type: types.ACTIVATE_ONLINE,
    payload: license_code,
  }),
  deactivateOnline: (): ProtectionAction => ({
    type: types.DEACTIVATE_ONLINE,
  }),
  activationCertificateRequest: (
    license_code: LicenseCode
  ): ProtectionAction<LicenseCode> => ({
    type: types.ACTIVATE_CERT_REQUEST,
    payload: license_code,
  }),
  activationCertificateReset: (): ProtectionAction => ({
    type: types.ACTIVATE_CERT_REQUEST_RESET,
  }),
  activateOffline: (
    certificate: ActivationCertificate
  ): ProtectionAction<ActivationCertificate> => ({
    type: types.MANUAL_ACTIVATION,
    payload: certificate,
  }),
  deactivationCertificateRequest: (): ProtectionAction => ({
    type: types.DEACTIVATION_CERT_REQUEST,
  }),
  // Used to set the license status
  setStatus: (
    status: LicenseStatusResponse
  ): ProtectionAction<LicenseStatusResponse> => ({
    type: types.FETCH_STATUS_FULLFILLED,
    payload: status,
  }),
};

const mountPath = 'protection'; // mount point in global state, must match root reducer

const initialState: ProtectionState = {
  status: null,
  status_error: null,
  status_pending: false,

  action_result_type: null,
  action_results: null,
  action_started: false,
  action_error: null,
  is_pro: false,
  has_tsr: false,
};

function reducer(
  state: ProtectionState = initialState,
  action: ProtectionAction
): ProtectionState {
  switch (action.type) {
    case types.ACTION_STARTED: {
      return {
        ...state,
        action_results: null,
        action_error: null,
        action_started: true,
        action_result_type: null,
      };
    }
    case types.ACTION_REJECTED: {
      const payload = payloadAs<ProtectionActionResultPayload>(action);
      return {
        ...state,
        action_results: null,
        action_error: payload.data,
        action_started: false,
        action_result_type: payload.type,
      };
    }
    case types.ACTION_FULLFILLED: {
      const payload = payloadAs<ProtectionActionResultPayload>(action);
      return {
        ...state,
        action_results: payload.data,
        action_error: null,
        action_started: false,
        action_result_type: payload.type,
      };
    }
    case types.FETCH_STATUS_STARTED: {
      return {
        ...state,
        status: null,
        status_error: null,
        status_pending: true,
      };
    }
    case types.FETCH_STATUS_REJECTED: {
      return {
        ...state,
        status: null,
        status_error: action.payload,
        status_pending: false,
      };
    }
    case types.FETCH_STATUS_FULLFILLED: {
      const status = payloadAs<LicenseStatusResponse>(action);
      return {
        ...state,
        status,
        status_error: null,
        status_pending: false,
        is_pro: status.features.includes(FEATURE_PRO),
        has_tsr: status.features.includes(FEATURE_TSR),
      };
    }
    default: {
      return state;
    }
  }
}

// selectors

const localSelectors = {
  status: (state: ProtectionState): LicenseStatusResponse | null =>
    state.status,
  // Use this for checking for application version. Basic / Pro
  is_pro: (state: ProtectionState): boolean => state.is_pro,
  has_tsr: (state: ProtectionState): boolean => state.has_tsr,
  is_cloud: (state: ProtectionState): boolean =>
    state.status?.cloud ? true : false,
};

const globalized = globalizeSelectors(localSelectors, mountPath);
const selectors = globalized as typeof globalized & {
  allow_matching: (state: any) => boolean;
  is_ist_pro: (state: any) => boolean;
};
selectors.allow_matching = (state: any): boolean =>
  Boolean(
    (state[mountPath].status?.features.includes(FEATURE_POS_MATCHING) &&
      !offline_mode &&
      state.user?.current_user?.privileges?.includes(PRIVILEGE_MATCHING)) ||
      false
  );

selectors.is_ist_pro = (state: any): boolean =>
  Boolean(
    state[mountPath].status?.cloud_features?.includes(FEATURE_IST_PRO) &&
      !offline_mode
  );
export { types as actionTypes, actions as default, selectors, reducer };
